import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getBytes,
  getDownloadURL,
  getStorage,
  listAll,
  ref as storageRef,
  uploadString,
} from "firebase/storage";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import {
  ChevronDown,
  FileText,
  FlaskConical,
  Loader2,
  Play,
  RefreshCw,
  Save,
  Settings2,
} from "lucide-react";
import { runPodcastSkillTest } from "../helpers/podcastSkillTest";
import { firestore } from "../helpers/firebaseConfig";
import "./PodcastGeneratorTestPage.css";

const BUCKET_URI = "gs://wui-go-97d87.appspot.com";
const ROOT = "skill-tests";
const SKILLS_PATH = `${ROOT}/skills-v1`;
const SKILLS_CONTENT_PATH = `${SKILLS_PATH}/skills`;
const RUNS_PATH = `${ROOT}/runs`;
const INPUTS_PATH = `${ROOT}/inputs`;
const POLL_INTERVAL = 10000;
const STATUS_COLLECTION = "podcast_gen_test";

const decode = (bytes) => new TextDecoder().decode(bytes);
const readText = async (storage, path) => decode(await getBytes(storageRef(storage, path)));
const safeReadText = async (storage, path) => readText(storage, path).catch(() => "");
const iterationNumber = (name) => Number(String(name).match(/iteration_(\d+)/)?.[1] || 0);

const USED_SKILL_FILES = [
  { path: "skill_01_dialog_outline_generator.md", label: "Outline generator" },
  { path: "skill_02_dialog_performance_polisher.md", label: "Performance polisher" },
  { path: "evaluators/generator/source_preservation.md", label: "Source preservation" },
  { path: "evaluators/generator/question_handling.md", label: "Question handling" },
  { path: "evaluators/generator/answer_handling.md", label: "Answer handling" },
  { path: "evaluators/generator/overall_conversation.md", label: "Overall conversation" },
  { path: "evaluators/polisher/speech_patterns.md", label: "Speech patterns" },
  { path: "evaluators/polisher/content_fidelity.md", label: "Content fidelity" },
  { path: "evaluators/polisher/speech_fillers.md", label: "Speech fillers" },
].map((file) => ({ ...file, fullPath: `${SKILLS_CONTENT_PATH}/${file.path}` }));

async function loadStage(storage, runPath, stage) {
  const stagePath = `${runPath}/${stage}`;
  let listing;
  try {
    listing = await listAll(storageRef(storage, stagePath));
  } catch (error) {
    if (error?.code === "storage/object-not-found") return { iterations: [], final: "", error: "" };
    return { iterations: [], final: "", error: `${stage}: ${error.message || "could not list files"}` };
  }
  const finalItem = listing.items.find((item) => item.name === "final.txt");
  const iterations = await Promise.all(
    listing.prefixes
      .filter((prefix) => /^iteration_\d+$/.test(prefix.name))
      .sort((a, b) => iterationNumber(a.name) - iterationNumber(b.name))
      .map(async (prefix) => {
        const files = await listAll(prefix);
        const contents = {};
        const readErrors = [];
        await Promise.all(files.items.map(async (item) => {
          try {
            contents[item.name] = decode(await getBytes(item));
          } catch (error) {
            readErrors.push(`${item.name}: ${error.message || "could not read file"}`);
          }
        }));
        return { name: prefix.name, files: contents, error: readErrors.join("; ") };
      })
  );
  let final = "";
  let error = "";
  if (finalItem) {
    try {
      final = decode(await getBytes(finalItem));
    } catch (readError) {
      error = `final.txt: ${readError.message || "could not read file"}`;
    }
  }
  return {
    iterations,
    final,
    error,
  };
}

async function loadRunSkillFiles(folderRef, rootPath = folderRef.fullPath) {
  let listing;
  try {
    listing = await listAll(folderRef);
  } catch (error) {
    if (error?.code === "storage/object-not-found") return { files: [], errors: [] };
    return { files: [], errors: [error.message || "Could not list the run's skill snapshot."] };
  }

  const files = [];
  const errors = [];
  await Promise.all(listing.items
    .filter((item) => item.name.toLowerCase().endsWith(".md"))
    .map(async (item) => {
      try {
        files.push({
          path: item.fullPath.slice(rootPath.length + 1),
          content: decode(await getBytes(item)),
        });
      } catch (error) {
        errors.push(`${item.name}: ${error.message || "could not read file"}`);
      }
    }));
  const nested = await Promise.all(listing.prefixes.map((prefix) => loadRunSkillFiles(prefix, rootPath)));
  nested.forEach((result) => {
    files.push(...result.files);
    errors.push(...result.errors);
  });
  files.sort((a, b) => a.path.localeCompare(b.path, undefined, { numeric: true }));
  return { files, errors };
}

async function loadRun(storage, prefix) {
  const [input, skillSnapshot, outline, polisher, mp3Url, wavUrl] = await Promise.all([
    safeReadText(storage, `${prefix.fullPath}/input.txt`),
    loadRunSkillFiles(storageRef(storage, `${prefix.fullPath}/skills`)),
    loadStage(storage, prefix.fullPath, "outline"),
    loadStage(storage, prefix.fullPath, "polisher"),
    getDownloadURL(storageRef(storage, `${prefix.fullPath}/audio/final.mp3`)).catch(() => ""),
    getDownloadURL(storageRef(storage, `${prefix.fullPath}/audio/final.wav`)).catch(() => ""),
  ]);
  const status = polisher.final ? "complete" : outline.final || polisher.iterations.length ? "running" : "submitted";
  return {
    id: prefix.name,
    path: prefix.fullPath,
    input,
    skillSnapshot: skillSnapshot.files,
    outline,
    polisher,
    mp3Url,
    wavUrl,
    status,
    storageError: [outline.error, polisher.error, ...skillSnapshot.errors].filter(Boolean).join("; "),
  };
}

function SkillSnapshot({ files }) {
  return (
    <section className="podcast-test-snapshot">
      <div className="podcast-test-stage-heading">
        <h4>Skill snapshot</h4>
        <span>{files.length} {files.length === 1 ? "file" : "files"}</span>
      </div>
      {!files.length ? <p className="podcast-test-muted">Waiting for the backend to snapshot the skill files…</p> : null}
      <div className="podcast-test-snapshot-files">
        {files.map((file) => <TextArtifact key={file.path} label={file.path} value={file.content} />)}
      </div>
    </section>
  );
}

function AudioStage({ run }) {
  const audioUrl = run.mp3Url || run.wavUrl;
  return (
    <section className="podcast-test-stage podcast-test-audio-stage">
      <div className="podcast-test-stage-heading"><h4>Final podcast</h4><span>{audioUrl ? "Ready" : "Waiting for output…"}</span></div>
      {run.speakerVoices || run.speakerWordCounts ? (
        <div className="podcast-test-speakers">
          {["Alex", "Jordan"].map((speaker) => (
            <div key={speaker}>
              <strong>{speaker}</strong>
              <span>{run.speakerVoices?.[speaker] || "Voice pending"}</span>
              <small>{run.speakerWordCounts?.[speaker] != null ? `${run.speakerWordCounts[speaker]} spoken words` : "Word count pending"}</small>
            </div>
          ))}
        </div>
      ) : null}
      {audioUrl ? <audio className="podcast-test-player" controls preload="metadata" src={audioUrl} /> : <p className="podcast-test-muted">The audio files have not appeared in Storage yet.</p>}
      <div className="podcast-test-audio-links">
        {run.mp3Url ? <a href={run.mp3Url} target="_blank" rel="noreferrer">Download MP3</a> : null}
        {run.wavUrl ? <a href={run.wavUrl} target="_blank" rel="noreferrer">Download WAV</a> : null}
      </div>
    </section>
  );
}

function TextArtifact({ label, value }) {
  if (!value) return null;
  return (
    <details className="podcast-test-artifact">
      <summary><FileText size={15} />{label}<ChevronDown size={15} /></summary>
      <pre>{value}</pre>
    </details>
  );
}

function Iteration({ iteration }) {
  return (
    <details className="podcast-test-iteration">
      <summary>{iteration.name.replace("_", " ")}<span>{Object.keys(iteration.files).length} files</span></summary>
      <div className="podcast-test-artifacts">
        {iteration.error ? <div className="podcast-test-run-error">{iteration.error}</div> : null}
        <TextArtifact label="Input" value={iteration.files["input.txt"]} />
        <TextArtifact label="Output" value={iteration.files["output.txt"]} />
        <TextArtifact label="Critics" value={iteration.files["critics.txt"]} />
      </div>
    </details>
  );
}

function Stage({ title, data }) {
  return (
    <section className="podcast-test-stage">
      <div className="podcast-test-stage-heading">
        <h4>{title}</h4>
        <span>{data.iterations.length} {data.iterations.length === 1 ? "iteration" : "iterations"}</span>
      </div>
      {!data.iterations.length && !data.final ? <p className="podcast-test-muted">Waiting for output…</p> : null}
      {data.iterations.map((iteration) => <Iteration key={iteration.name} iteration={iteration} />)}
      <TextArtifact label="Final" value={data.final} />
    </section>
  );
}

function RunCard({ run }) {
  return (
    <article className="podcast-test-run">
      <details>
        <summary className="podcast-test-run-summary">
          <div><strong>{run.id}</strong><span>{run.execution || run.path}</span></div>
          <div className="podcast-test-run-state">
            {run.currentStage ? <span className="podcast-test-current-stage">{run.currentStage}</span> : null}
            <span className={`podcast-test-status is-${run.status}`}>{run.status.replaceAll("_", " ")}</span>
          </div>
        </summary>
        <div className="podcast-test-run-body">
          {run.error ? <div className="podcast-test-run-error" role="alert">{run.error}</div> : null}
          {run.storageError ? <div className="podcast-test-run-error" role="alert">Storage: {run.storageError}</div> : null}
          <TextArtifact label="Run input" value={run.input} />
          <SkillSnapshot files={run.skillSnapshot || []} />
          <div className="podcast-test-stage-grid">
            <Stage title="Outline generator" data={run.outline} />
            <Stage title="Performance polisher" data={run.polisher} />
          </div>
          <AudioStage run={run} />
        </div>
      </details>
    </article>
  );
}

export default function PodcastGeneratorTestPage() {
  const storage = useMemo(() => getStorage(), []);
  const [testName, setTestName] = useState("");
  const [input, setInput] = useState("");
  const [runs, setRuns] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [isLoadingRuns, setIsLoadingRuns] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [skillFiles, setSkillFiles] = useState([]);
  const [skills, setSkills] = useState({});
  const [originalSkills, setOriginalSkills] = useState({});
  const [activeSkill, setActiveSkill] = useState("");
  const [isLoadingSkills, setIsLoadingSkills] = useState(true);
  const [isSavingSkills, setIsSavingSkills] = useState(false);

  const loadSkills = useCallback(async () => {
    setIsLoadingSkills(true);
    try {
      const entries = await Promise.all(USED_SKILL_FILES.map(async (file) => [file.path, await readText(storage, file.fullPath)]));
      const next = Object.fromEntries(entries);
      setSkillFiles(USED_SKILL_FILES);
      setSkills(next);
      setOriginalSkills(next);
      setActiveSkill((current) => USED_SKILL_FILES.some((file) => file.path === current) ? current : USED_SKILL_FILES[0].path);
    } catch (error) {
      setSkillFiles([]);
      setSkills({});
      setOriginalSkills({});
      setActiveSkill("");
      setMessage({ type: "error", text: error.message || "Could not list or read the skill bundle." });
    } finally {
      setIsLoadingSkills(false);
    }
  }, [storage]);

  const loadRuns = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setIsLoadingRuns(true);
    try {
      const listing = await listAll(storageRef(storage, RUNS_PATH));
      const results = await Promise.allSettled(listing.prefixes.map((prefix) => loadRun(storage, prefix)));
      const loaded = results.map((result, index) => result.status === "fulfilled"
        ? result.value
        : {
          id: listing.prefixes[index].name,
          path: listing.prefixes[index].fullPath,
          input: "",
          skillSnapshot: [],
          outline: { iterations: [], final: "" },
          polisher: { iterations: [], final: "" },
          mp3Url: "",
          wavUrl: "",
          status: "storage_error",
          storageError: result.reason?.message || "Could not load this run's files.",
        });
      loaded.sort((a, b) => b.id.localeCompare(a.id, undefined, { numeric: true }));
      setRuns(loaded);
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Could not load test runs." });
    } finally {
      if (!quiet) setIsLoadingRuns(false);
    }
  }, [storage]);

  useEffect(() => { loadSkills(); }, [loadSkills]);
  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(firestore, STATUS_COLLECTION), orderBy("created_at", "desc")),
      (snapshot) => setJobs(snapshot.docs.map((item) => ({ id: item.id, ...(item.data() || {}) }))),
      (error) => setMessage({ type: "error", text: error.message || "Could not load test job statuses." })
    );
    return unsubscribe;
  }, []);
  useEffect(() => {
    loadRuns();
    const interval = window.setInterval(() => loadRuns({ quiet: true }), POLL_INTERVAL);
    return () => window.clearInterval(interval);
  }, [loadRuns]);

  const dirtySkills = skillFiles.filter((file) => skills[file.path] !== originalSkills[file.path]);
  const displayedRuns = useMemo(() => {
    const storageById = new Map(runs.map((run) => [run.id, run]));
    const joined = jobs.map((job) => {
      const stored = storageById.get(job.id);
      storageById.delete(job.id);
      return {
        id: job.id,
        path: job.output_folder || stored?.path || `${RUNS_PATH}/${job.id}`,
        input: stored?.input || "",
        skillSnapshot: stored?.skillSnapshot || [],
        outline: stored?.outline || { iterations: [], final: "" },
        polisher: stored?.polisher || { iterations: [], final: "" },
        mp3Url: stored?.mp3Url || "",
        wavUrl: stored?.wavUrl || "",
        status: job.status || stored?.status || "submitted",
        execution: job.execution || "",
        error: job.error || "",
        storageError: stored?.storageError || "",
        currentStage: job.currentStage || "",
        outputs: job.outputs || {},
        speakerWordCounts: job.speakerWordCounts || {},
        speakerVoices: job.speakerVoices || {},
      };
    });
    return [...joined, ...storageById.values()];
  }, [jobs, runs]);

  const saveSkills = async () => {
    if (!dirtySkills.length || isSavingSkills) return;
    setIsSavingSkills(true);
    setMessage({ type: "", text: "" });
    try {
      await Promise.all(dirtySkills.map((file) => uploadString(
        storageRef(storage, file.fullPath),
        skills[file.path] || "",
        "raw",
        { contentType: "text/markdown; charset=utf-8" }
      )));
      setOriginalSkills({ ...skills });
      setMessage({ type: "success", text: `${dirtySkills.length} skill ${dirtySkills.length === 1 ? "file" : "files"} saved.` });
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Could not save skill files." });
    } finally {
      setIsSavingSkills(false);
    }
  };

  const submitRun = async (event) => {
    event.preventDefault();
    const trimmedInput = input.trim();
    if (!trimmedInput || isSubmitting) return;
    const baseName = (testName.trim() || "podcast-test")
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "") || "podcast-test";
    const stamp = new Date().toISOString().replace(/[-:TZ.]/g, "").slice(0, 14);
    const runId = `${baseName}-${stamp}`;
    const inputPath = `${INPUTS_PATH}/${runId}.txt`;
    const outputPath = `${RUNS_PATH}/${runId}`;
    const locations = {
      inputLocation: `${BUCKET_URI}/${inputPath}`,
      skillsFolder: `${BUCKET_URI}/${SKILLS_PATH}`,
      outputFolder: `${BUCKET_URI}/${outputPath}`,
      statusFirestoreLocation: `${STATUS_COLLECTION}/${runId}`,
    };
    const runDocument = doc(firestore, STATUS_COLLECTION, runId);

    setIsSubmitting(true);
    setMessage({ type: "", text: "" });
    try {
      await setDoc(runDocument, {
        run_id: runId,
        test_name: testName.trim() || runId,
        status: "submitting",
        execution: "",
        input_location: locations.inputLocation,
        skills_folder: locations.skillsFolder,
        output_folder: locations.outputFolder,
        status_firestore_location: locations.statusFirestoreLocation,
        error: "",
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
      await uploadString(storageRef(storage, inputPath), trimmedInput, "raw", { contentType: "text/plain; charset=utf-8" });
      const response = await runPodcastSkillTest(locations);
      await runTransaction(firestore, async (transaction) => {
        const latestRun = await transaction.get(runDocument);
        const latestStatus = latestRun.data()?.status;
        const backendAlreadyStarted = ["running", "succeeded", "failed"].includes(latestStatus);
        transaction.set(runDocument, {
          ...(!backendAlreadyStarted ? { status: response.status || "submitted" } : {}),
          execution: response.execution || "",
          submitted_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        }, { merge: true });
      });
      setMessage({ type: "success", text: `Run ${runId} was submitted. Results will appear as the backend writes them.` });
      setTestName("");
      setInput("");
      window.setTimeout(() => loadRuns({ quiet: true }), 2000);
    } catch (error) {
      const errorMessage = error.message || "Could not submit the podcast test.";
      await setDoc(runDocument, {
        status: "submission_failed",
        error: errorMessage,
        completed_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      }, { merge: true }).catch(() => {});
      setMessage({ type: "error", text: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="podcast-test-page">
      <div className="podcast-test-shell">
        <header className="podcast-test-header">
          <div className="podcast-test-icon"><FlaskConical size={28} /></div>
          <div><p>Dialog skill lab</p><h1>Podcast generator tests</h1><span>Run and inspect the complete outline and performance-polishing workflow.</span></div>
        </header>

        <section className="podcast-test-skillbar">
          <div><Settings2 size={19} /><span><strong>Active skills</strong><small>{BUCKET_URI}/{SKILLS_CONTENT_PATH}</small></span></div>
          <div className="podcast-test-skill-count">{isLoadingSkills ? "Loading…" : `${skillFiles.length} files`}</div>
          <button type="button" onClick={() => setSkillsOpen((open) => !open)}>{skillsOpen ? "Close editor" : "Edit skills"}</button>
        </section>

        {skillsOpen ? (
          <section className="podcast-test-skill-editor">
            <aside>{skillFiles.map((file) => {
              const dirty = skills[file.path] !== originalSkills[file.path];
              return <button className={activeSkill === file.path ? "is-active" : ""} type="button" key={file.path} onClick={() => setActiveSkill(file.path)}>{file.label}{dirty ? <i>Edited</i> : null}</button>;
            })}</aside>
            <div className="podcast-test-editor-main">
              <div><strong>{skillFiles.find((file) => file.path === activeSkill)?.label || "No skill selected"}</strong><code>{activeSkill}</code></div>
              {isLoadingSkills ? <div className="podcast-test-editor-loading"><Loader2 className="podcast-test-spin" />Loading skill…</div> : (
                activeSkill
                  ? <textarea value={skills[activeSkill] || ""} onChange={(event) => setSkills((current) => ({ ...current, [activeSkill]: event.target.value }))} spellCheck="false" />
                  : <div className="podcast-test-editor-loading">No Markdown skill files found.</div>
              )}
              <footer><span>{dirtySkills.length ? `${dirtySkills.length} unsaved` : "All changes saved"}</span><button type="button" disabled={!dirtySkills.length || isSavingSkills} onClick={saveSkills}><Save size={16} />{isSavingSkills ? "Saving…" : "Save changes"}</button></footer>
            </div>
          </section>
        ) : null}

        <form className="podcast-test-form" onSubmit={submitRun}>
          <div className="podcast-test-form-heading"><div><h2>New end-to-end test</h2><p>The backend will snapshot the active skills and run every outline and polishing iteration.</p></div><Play size={21} /></div>
          <label><span>Test name</span><input value={testName} onChange={(event) => setTestName(event.target.value)} placeholder="e.g. node_653" /></label>
          <label><span>Input</span><textarea value={input} onChange={(event) => setInput(event.target.value)} placeholder="Paste the source material for the podcast…" rows={10} required /></label>
          <button className="podcast-test-submit" type="submit" disabled={!input.trim() || isSubmitting}>{isSubmitting ? <Loader2 className="podcast-test-spin" size={17} /> : <Play size={17} />}{isSubmitting ? "Submitting…" : "Run podcast test"}</button>
        </form>

        {message.text ? <div className={`podcast-test-message is-${message.type}`} role="status">{message.text}</div> : null}

        <section className="podcast-test-runs-section">
          <div className="podcast-test-runs-heading"><div><h2>Test runs</h2><span>{displayedRuns.length} runs</span></div><button type="button" disabled={isLoadingRuns} onClick={() => loadRuns()}><RefreshCw className={isLoadingRuns ? "podcast-test-spin" : ""} size={16} />Refresh</button></div>
          {isLoadingRuns ? <div className="podcast-test-empty"><Loader2 className="podcast-test-spin" />Loading runs…</div> : null}
          {!isLoadingRuns && !displayedRuns.length ? <div className="podcast-test-empty">No skill-test runs found.</div> : null}
          <div className="podcast-test-runs">{displayedRuns.map((run) => <RunCard key={run.id} run={run} />)}</div>
        </section>
      </div>
    </main>
  );
}
